import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  InputNumber,
  Divider,
  Typography,
  Row,
  Col,
  Space,
  Alert,
  notification,
} from 'antd';
import {
  SettingOutlined,
  SecurityScanOutlined,
  GlobalOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { settingsService, SystemSettings, SecuritySettings, EmailSettings } from '../../services/settingsService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const SuperAdminSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [systemForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [emailForm] = Form.useForm();

  // Load current settings on component mount
  useEffect(() => {
    loadCurrentSettings();
  }, []);

  const loadCurrentSettings = async () => {
    try {
      setLoading(true);
      
      // Load all settings from API
      const response = await settingsService.getSettings();
      
      // Flatten the nested settings structure into a simple key-value map
      const settingsMap: Record<string, any> = {};
      Object.values(response.settings).forEach(category => {
        Object.entries(category).forEach(([key, setting]) => {
          settingsMap[key] = setting.value;
        });
      });

      // Populate system form
      systemForm.setFieldsValue({
        siteName: settingsMap.app_name || 'FleetBase',
        siteUrl: settingsMap.site_url || 'https://fleetbase.lab.arkod.fr',
        contactEmail: settingsMap.contact_email || 'contact@arkod.fr',
        supportEmail: settingsMap.support_email || 'support@arkod.fr',
        maintenanceMode: settingsMap.maintenance_mode || false,
        allowRegistration: settingsMap.allow_registration ?? true,
        maxTenantsPerRegion: settingsMap.max_tenants_per_region || 100,
        defaultTenantLimits: {
          maxVehicles: settingsMap.default_max_vehicles || 10,
          maxDrivers: settingsMap.default_max_drivers || 20,
          maxMissionsPerMonth: settingsMap.default_max_missions_per_month || 500,
        },
      });

      // Populate security form - only set values for fields that exist in the UI
      securityForm.setFieldsValue({
        passwordPolicy: {
          minLength: settingsMap.password_min_length || 8,
          requireUppercase: settingsMap.password_require_uppercase ?? true,
          requireNumbers: settingsMap.password_require_numbers ?? true,
          requireSymbols: settingsMap.password_require_symbols || false,
          expireDays: settingsMap.password_expire_days || 90,
        },
        sessionTimeout: settingsMap.session_timeout_minutes || 480,
        maxLoginAttempts: settingsMap.max_login_attempts || 5,
        twoFactorRequired: settingsMap.require_2fa || false,
        // Remove ipWhitelist since there's no form field for it
      });

      // Populate email form
      emailForm.setFieldsValue({
        smtpHost: settingsMap.smtp_host || '',
        smtpPort: settingsMap.smtp_port || 587,
        smtpUser: settingsMap.smtp_user || '',
        smtpPassword: settingsMap.smtp_password || '',
        smtpSecure: settingsMap.smtp_secure ?? true,
        fromEmail: settingsMap.email_from_address || 'noreply@arkod.fr',
        fromName: settingsMap.smtp_from_name || 'FleetBase',
        templatesEnabled: settingsMap.email_templates_enabled ?? true,
      });

    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      notification.error({
        message: 'Erreur de chargement',
        description: 'Impossible de charger les paramètres système. Les valeurs par défaut seront utilisées.',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSettings = async (values: SystemSettings) => {
    setLoading(true);
    try {
      await settingsService.updateSystemSettings(values);
      
      notification.success({
        message: 'Paramètres système sauvegardés',
        description: 'Les paramètres système ont été mis à jour avec succès.',
        duration: 3,
      });

    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde des paramètres système:', error);
      
      notification.error({
        message: 'Erreur de sauvegarde',
        description: error.response?.data?.error || 'Une erreur est survenue lors de la sauvegarde des paramètres système.',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySettings = async (values: SecuritySettings) => {
    setLoading(true);
    try {
      await settingsService.updateSecuritySettings(values);
      
      notification.success({
        message: 'Paramètres de sécurité sauvegardés',
        description: 'Les paramètres de sécurité ont été mis à jour avec succès.',
        duration: 3,
      });

    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde des paramètres de sécurité:', error);
      
      notification.error({
        message: 'Erreur de sauvegarde',
        description: error.response?.data?.error || 'Une erreur est survenue lors de la sauvegarde des paramètres de sécurité.',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSettings = async (values: EmailSettings) => {
    setLoading(true);
    try {
      await settingsService.updateEmailSettings(values);
      
      notification.success({
        message: 'Configuration email sauvegardée',
        description: 'La configuration email a été mise à jour avec succès.',
        duration: 3,
      });

    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde de la configuration email:', error);
      
      notification.error({
        message: 'Erreur de sauvegarde',
        description: error.response?.data?.error || 'Une erreur est survenue lors de la sauvegarde de la configuration email.',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmailConfig = async () => {
    try {
      setTestingEmail(true);
      const values = emailForm.getFieldsValue() as EmailSettings;
      
      const result = await settingsService.testEmailConfig(values);
      
      if (result.success) {
        notification.success({
          message: 'Test email réussi',
          description: result.message || 'La configuration email fonctionne correctement.',
          duration: 3,
        });
      } else {
        notification.warning({
          message: 'Test email échoué',
          description: result.message || 'La configuration email ne semble pas fonctionner.',
          duration: 5,
        });
      }

    } catch (error: any) {
      console.error('Erreur lors du test email:', error);
      
      notification.error({
        message: 'Erreur de test',
        description: error.response?.data?.error || 'Impossible de tester la configuration email.',
        duration: 5,
      });
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <SettingOutlined style={{ marginRight: '8px' }} />
        Configuration Système
      </Title>
      <Text type="secondary">
        Gestion des paramètres globaux de la plateforme FleetBase
      </Text>

      <Card style={{ marginTop: '24px' }}>
        <Tabs defaultActiveKey="system" size="large">
          {/* Paramètres Système */}
          <TabPane 
            tab={<span><GlobalOutlined />Système</span>} 
            key="system"
          >
            <Alert 
              message="Paramètres Globaux" 
              description="Ces paramètres affectent l'ensemble de la plateforme et tous les tenants."
              type="info" 
              showIcon 
              style={{ marginBottom: '24px' }}
            />
            
            <Form
              form={systemForm}
              layout="vertical"
              onFinish={handleSystemSettings}
              disabled={loading}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Nom du site"
                    name="siteName"
                    rules={[{ required: true, message: 'Le nom du site est requis' }]}
                  >
                    <Input placeholder="FleetBase" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="URL du site"
                    name="siteUrl"
                    rules={[{ required: true, message: 'L\'URL du site est requise' }]}
                  >
                    <Input placeholder="https://fleetbase.lab.arkod.fr" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Email de contact"
                    name="contactEmail"
                    rules={[{ required: true, type: 'email' }]}
                  >
                    <Input placeholder="contact@arkod.fr" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email de support"
                    name="supportEmail"
                    rules={[{ required: true, type: 'email' }]}
                  >
                    <Input placeholder="support@arkod.fr" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Paramètres d'accès</Divider>

              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item
                    label="Mode maintenance"
                    name="maintenanceMode"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Autoriser les inscriptions"
                    name="allowRegistration"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Max tenants par région"
                    name="maxTenantsPerRegion"
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Limites par défaut pour nouveaux tenants</Divider>

              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item
                    label="Véhicules max"
                    name={['defaultTenantLimits', 'maxVehicles']}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Chauffeurs max"
                    name={['defaultTenantLimits', 'maxDrivers']}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Missions/mois max"
                    name={['defaultTenantLimits', 'maxMissionsPerMonth']}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Sauvegarder les paramètres système
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          {/* Paramètres Sécurité */}
          <TabPane 
            tab={<span><SecurityScanOutlined />Sécurité</span>} 
            key="security"
          >
            <Alert 
              message="Paramètres de Sécurité" 
              description="Configuration des politiques de sécurité globales."
              type="warning" 
              showIcon 
              style={{ marginBottom: '24px' }}
            />

            <Form
              form={securityForm}
              layout="vertical"
              onFinish={handleSecuritySettings}
              disabled={loading}
            >
              <Title level={4}>Politique des mots de passe</Title>
              
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item
                    label="Longueur minimale"
                    name={['passwordPolicy', 'minLength']}
                  >
                    <InputNumber min={6} max={32} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Expiration (jours)"
                    name={['passwordPolicy', 'expireDays']}
                  >
                    <InputNumber min={30} max={365} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Tentatives max"
                    name="maxLoginAttempts"
                  >
                    <InputNumber min={3} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item
                    label="Majuscules requises"
                    name={['passwordPolicy', 'requireUppercase']}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Chiffres requis"
                    name={['passwordPolicy', 'requireNumbers']}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Symboles requis"
                    name={['passwordPolicy', 'requireSymbols']}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="2FA obligatoire"
                    name="twoFactorRequired"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Sessions</Divider>

              <Form.Item
                label="Timeout de session (minutes)"
                name="sessionTimeout"
              >
                <InputNumber min={15} max={480} style={{ width: '200px' }} />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Sauvegarder les paramètres de sécurité
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          {/* Paramètres Email */}
          <TabPane 
            tab={<span><MailOutlined />Email</span>} 
            key="email"
          >
            <Alert 
              message="Configuration SMTP" 
              description="Paramètres pour l'envoi d'emails système."
              type="info" 
              showIcon 
              style={{ marginBottom: '24px' }}
            />

            <Form
              form={emailForm}
              layout="vertical"
              onFinish={handleEmailSettings}
              disabled={loading}
            >
              <Title level={4}>Configuration SMTP</Title>
              
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Serveur SMTP"
                    name="smtpHost"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="smtp.gmail.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Port SMTP"
                    name="smtpPort"
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={9999} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Utilisateur SMTP"
                    name="smtpUser"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="votre-email@gmail.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Mot de passe SMTP"
                    name="smtpPassword"
                    rules={[{ required: true }]}
                  >
                    <Input.Password placeholder="Mot de passe d'application" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Email expéditeur"
                    name="fromEmail"
                    rules={[{ required: true, type: 'email' }]}
                  >
                    <Input placeholder="noreply@arkod.fr" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Nom expéditeur"
                    name="fromName"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="FleetBase" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Connexion sécurisée (TLS)"
                    name="smtpSecure"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Templates activés"
                    name="templatesEnabled"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Sauvegarder la configuration email
                  </Button>
                  <Button onClick={handleTestEmailConfig} loading={testingEmail}>
                    Tester la configuration
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default SuperAdminSettings;